import { BRAND, DEFAULT_OG_IMAGE, LOCATIONS } from './siteConfig';

import coreAuthority from './data/coreAuthority';
import gender from './data/gender';
import general from './data/general';
import substance from './data/substance';
import referral from './data/referral';
import locations from './data/locations';
import educational from './data/educational';
import family from './data/family';
import funding from './data/funding';
import hubs from './data/hubs';

const RAW = [
  ...coreAuthority, ...gender, ...general, ...substance, ...referral,
  ...locations, ...educational, ...family, ...funding, ...hubs,
];

// ---- Silo hub definitions -------------------------------------------------
const HUB = {
  recovery: { label: 'Recovery Housing', path: '/recovery-housing/' },
  men: { label: "Men's Housing", path: '/mens-recovery-housing/' },
  women: { label: "Women's Housing", path: '/womens-recovery-housing/' },
  locations: { label: 'Locations', path: '/recovery-housing-lehigh-valley-pa/' },
  funding: { label: 'Funding Options', path: '/recovery-housing-funding-assistance/' },
  resources: { label: 'Resources', path: '/resources/' },
};

function siloFor(p) {
  if (p.category === 'Funding / Scholarship Page') return HUB.funding;
  if (p.category === 'Educational Resource') return HUB.resources;
  if (p.category === 'Regional Hub' || p.category === 'Location / House Page') return HUB.locations;
  if (p.gender === 'men') return HUB.men;
  if (p.gender === 'women') return HUB.women;
  return HUB.recovery;
}

const EYEBROW = {
  'Core Authority Page': 'Recovery Housing',
  'Gender-Specific Page': 'Recovery Housing',
  'General Sober Living / Recovery': 'Recovery Housing',
  'Substance-Specific Page': 'Recovery Housing',
  'Referral / Partner Page': 'Referrals & Next Steps',
  'Regional Hub': 'Locations',
  'Location / House Page': 'Locations',
  'Educational Resource': 'Resources',
  'Family Search Intent': 'Family Guidance',
  'Funding / Scholarship Page': 'Funding & Scholarships',
  Hub: 'Recovery Housing',
};

const CTA_LABEL = {
  'Core Authority Page': 'Apply for Recovery Housing',
  'Gender-Specific Page': 'Apply for Recovery Housing',
  'General Sober Living / Recovery': 'Apply for Recovery Housing',
  'Substance-Specific Page': 'Apply for Recovery Housing',
  'Referral / Partner Page': 'Ask About Availability',
  'Regional Hub': 'Ask About Availability',
  'Location / House Page': 'Ask About Availability',
  'Educational Resource': 'Contact PGSRH',
  'Family Search Intent': 'Contact PGSRH',
  'Funding / Scholarship Page': 'Ask About Funding Options',
  Hub: 'Apply for Recovery Housing',
};

const COMPLIANCE =
  'PGSRH provides structured recovery housing and a supportive, peer-based living environment. ' +
  'We are not a detox, rehab, treatment, therapy, counseling, or medical facility, and we do not ' +
  'provide clinical or medical services. Residents access any clinical care through their own outside ' +
  'providers.';

// ---- Contextual "How PGSRH may help" (varied by category) -----------------
function buildHowWeHelp(p) {
  const kw = (p.primaryKeyword || 'recovery housing').toLowerCase();
  const place = p.city ? `${p.city}, PA` : BRAND.region;
  const genderWord = p.gender === 'men' ? 'men' : p.gender === 'women' ? 'women' : 'men and women';

  const opener = {
    'Referral / Partner Page':
      `PGSRH works with individuals, families, treatment teams, case managers, and referral partners to arrange ${kw} in ${place}. ` +
      `We focus on a smooth, respectful transition into a stable, substance-free home where accountability and routine come first.`,
    'Funding / Scholarship Page':
      `PGSRH offers structured recovery housing for ${genderWord} in ${place} and can talk with you about ${kw} and the current cost of a bed. ` +
      `We keep the conversation simple: what a stay involves, what it costs, and what options may help make it manageable.`,
    'Educational Resource':
      `This page is here to help you understand ${kw}. When you are ready, PGSRH provides real recovery housing for ${genderWord} in ${place} ` +
      `built around structure, peer support, and daily accountability.`,
    'Family Search Intent':
      `If you are searching on behalf of someone you love, PGSRH can walk you through how ${kw} works, what a home is like day to day, ` +
      `and how to help your family member apply. We know these calls are hard, and we treat them with care.`,
  }[p.category] ||
    `PGSRH provides ${kw} for ${genderWord} in ${place}. Our homes are built around structure, peer support, house expectations, ` +
    `and daily accountability so residents can focus on rebuilding routine, responsibility, and community.`;

  const closer =
    `Getting started is straightforward. You can apply online, call us, or ask a case manager or treatment team to reach out on your behalf. ` +
    `We will explain current availability, what living here involves, and the next steps. To ask about a bed or begin an application, ` +
    `call ${p.phone || BRAND.generalPhone} or use our online application.`;

  return [opener, closer];
}

function buildFunding(p) {
  if (!p.funding) return null;
  const place = p.city ? `${p.city}, PA` : BRAND.region;
  return {
    heading: p.fundingHeading || 'Funding & scholarship options',
    paragraphs: [
      `Cost should not be the only thing that stops someone from finding stable recovery housing. Depending on eligibility ` +
        `and current availability, funding or scholarship options may be available to help with the cost of ${p.primaryKeyword ? p.primaryKeyword.toLowerCase() : 'recovery housing'} in ${place}.`,
      `County, state, scholarship, and other assistance programs each have their own requirements, and availability can change. ` +
        `PGSRH can discuss current payment and referral options with you and explain what may apply to your situation. We do not ` +
        `guarantee approval, free housing, or any specific funding outcome.`,
    ],
    note:
      'Funding or scholarship options may be available depending on eligibility and current availability. ' +
      'Availability must be confirmed, and approval is never guaranteed. Contact PGSRH to ask what options may apply.',
  };
}

function buildLocation(p) {
  if (!p.city || !LOCATIONS[p.city]) return null;
  return LOCATIONS[p.city];
}

function phoneFor(p) {
  if (p.city && LOCATIONS[p.city]) return LOCATIONS[p.city].phone;
  if (p.gender === 'men') return BRAND.menPhone;
  return BRAND.generalPhone;
}

// ---- Related internal links ----------------------------------------------
let LOOKUP = null;
function lookup() {
  if (LOOKUP) return LOOKUP;
  LOOKUP = {};
  RAW.forEach((p) => {
    LOOKUP[p.slug] = { label: p.name, blurb: p.blurb || p.purpose || '' };
  });
  return LOOKUP;
}

function buildRelated(p) {
  const lk = lookup();
  const out = [];
  const push = (slug) => {
    if (slug && slug !== p.slug && lk[slug] && !out.find((o) => o.path === slug)) {
      out.push({ path: slug, label: lk[slug].label, blurb: lk[slug].blurb });
    }
  };

  (p.related || []).forEach(push);

  // Silo-aware defaults
  if (p.gender === 'men') {
    ['/mens-recovery-housing/', '/mens-recovery-housing-whitehall-pa/', '/recovery-housing/'].forEach(push);
  } else if (p.gender === 'women') {
    ['/womens-recovery-housing/', '/womens-recovery-housing-catasauqua-pa/', '/recovery-housing/'].forEach(push);
  }
  if (p.city === 'Whitehall') ['/recovery-housing-whitehall-pa/', '/recovery-housing-lehigh-valley-pa/'].forEach(push);
  if (p.city === 'Catasauqua') ['/recovery-housing-catasauqua-pa/', '/recovery-housing-lehigh-valley-pa/'].forEach(push);
  if (p.category === 'Funding / Scholarship Page') ['/recovery-housing-funding-assistance/', '/recovery-housing-payment-assistance/', '/recovery-housing-scholarships-pennsylvania/'].forEach(push);
  if (p.category === 'Referral / Partner Page') ['/housing-after-rehab/', '/recovery-housing-after-treatment/', '/recovery-housing-referrals/'].forEach(push);
  if (p.category === 'Educational Resource') ['/what-is-recovery-housing/', '/recovery-housing-faq/', '/benefits-of-recovery-housing/'].forEach(push);
  if (p.category === 'Family Search Intent') ['/family-guide-recovery-housing/', '/help-loved-one-find-recovery-housing/', '/recovery-housing/'].forEach(push);

  // Always give people the core hubs
  ['/recovery-housing/', '/recovery-housing-lehigh-valley-pa/', '/recovery-housing-funding-assistance/'].forEach(push);

  return out.slice(0, 6);
}

function buildBreadcrumbs(p) {
  const crumbs = [{ label: 'Home', path: '/' }];
  const silo = siloFor(p);
  if (silo.path !== p.slug) crumbs.push({ label: silo.label, path: silo.path });
  crumbs.push({ label: p.name, path: p.slug });
  return crumbs;
}

function buildSchema(p) {
  if (p.city && LOCATIONS[p.city]) {
    const l = LOCATIONS[p.city];
    return { type: 'LocalBusiness', data: { name: `${BRAND.name} \u2013 ${l.label}`, street: l.hasStreet ? l.street : '', city: l.city, state: l.state, zip: l.zip, phone: l.phone } };
  }
  if (p.category === 'Educational Resource') return { type: 'Article', data: {} };
  return { type: 'Service', data: { serviceType: p.primaryKeyword || 'Recovery Housing', name: p.name } };
}

// ---- Hero imagery for location + hub pages (optimized, overlaid) ----------
const IMG = {
  general: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=1500&q=68',
  men: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1500&q=68',
  women: 'https://images.pexels.com/photos/34963015/pexels-photo-34963015.jpeg?auto=compress&cs=tinysrgb&w=1500',
  region: 'https://images.unsplash.com/photo-1555636222-cae831e670b3?auto=format&fit=crop&w=1500&q=68',
  funding: 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?auto=format&fit=crop&w=1500&q=68',
  resources: 'https://images.unsplash.com/photo-1631510390389-c1e4fb20ff31?auto=format&fit=crop&w=1500&q=68',
};

function heroImageFor(p) {
  const HUBS = ['/recovery-housing/', '/mens-recovery-housing/', '/womens-recovery-housing/', '/recovery-housing-funding-assistance/', '/resources/'];
  const isLocation = p.category === 'Regional Hub' || p.category === 'Location / House Page';
  if (!HUBS.includes(p.slug) && !isLocation) return null;
  if (p.slug === '/resources/') return IMG.resources;
  if (p.slug === '/recovery-housing-funding-assistance/') return IMG.funding;
  if (p.gender === 'men') return IMG.men;
  if (p.gender === 'women') return IMG.women;
  if (p.category === 'Regional Hub' || p.category === 'Location / House Page') return IMG.region;
  return IMG.general;
}

function ctaSub(p) {  return (
    p.ctaSub ||
    `Speak with PGSRH about ${(p.primaryKeyword || 'recovery housing').toLowerCase()}, current availability, and next steps. ` +
    `Call ${phoneFor(p)} or apply online \u2014 individuals, families, and referral partners are all welcome to reach out.`
  );
}

// ---- Public API -----------------------------------------------------------
function compose(p) {
  const phone = phoneFor(p);
  const withPhone = { ...p, phone };
  return {
    slug: p.slug,
    canonicalPath: p.slug,
    title: p.title,
    description: p.description,
    image: p.image || DEFAULT_OG_IMAGE,
    eyebrow: EYEBROW[p.category] || 'Recovery Housing',
    h1: p.h1 || p.name,
    heroSubtitle: p.heroSubtitle,
    heroImage: heroImageFor(p),
    breadcrumbs: buildBreadcrumbs(p),
    intro: p.intro || [],
    complianceNote: COMPLIANCE,
    whoFor: p.whoFor ? { heading: p.whoForHeading || 'Who this recovery housing is for', items: p.whoFor } : null,
    sections: p.sections || [],
    howWeHelp: buildHowWeHelp(withPhone),
    funding: buildFunding(p),
    location: buildLocation(p),
    related: buildRelated(p),
    faqs: p.faqs || [],
    cta: { label: p.cta || CTA_LABEL[p.category] || 'Apply for Recovery Housing', sub: ctaSub(withPhone), headline: p.ctaHeadline },
    applyLink: p.category === 'Funding / Scholarship Page' ? '/apply#apply-form' : '/apply',
    phone,
    schema: buildSchema(p),
  };
}

const MAP = {};
RAW.forEach((p) => { MAP[p.slug] = compose(p); });

export function getPage(slug) {
  if (MAP[slug]) return MAP[slug];
  const alt = slug.endsWith('/') ? slug.slice(0, -1) : slug + '/';
  return MAP[alt] || null;
}

export function getAllPages() { return RAW.map((p) => MAP[p.slug]); }
export function getAllSlugs() { return RAW.map((p) => p.slug); }
export { HUB };
